// src/components/auth/SignUpForm.tsx
import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Google,
} from "@mui/icons-material";
import { useAuthStore } from "../../stores/authStore";
import { Privacy } from "../../pages/Privacy";
import { Terms } from "../../pages/Terms";
import { useT } from '../../i18n/useT';

interface SignUpFormProps {
  onSuccess: () => void;
  onSwitchToLogin: () => void;
  isUpgrade?: boolean;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({
  onSuccess,
  onSwitchToLogin,
  isUpgrade = false,
}) => {
  const t = useT();
  const [formData, setFormData] = useState({
    displayName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [ageVerification, setAgeVerification] = useState<
    "over16" | "under16" | ""
  >("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showAgeError, setShowAgeError] = useState(false);
  // After a successful signup, we briefly show a "check your email"
  // confirmation in-place before letting the parent close the modal.
  // This is important because the user might wonder if signup actually
  // worked, and helps land the email-verification expectation.
  const [signupComplete, setSignupComplete] = useState<{
    method: "email" | "google";
    email?: string;
  } | null>(null);

  const {
    signUpWithEmail,
    upgradeAnonymousAccount,
    clearError,
    signInWithGoogle,
  } = useAuthStore();

  const handleInputChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleAgeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as "over16" | "under16";
    setAgeVerification(value);

    // Show age restriction error immediately if under 16
    if (value === "under16") {
      setShowAgeError(true);
    } else {
      setShowAgeError(false);
    }
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) errors.push("At least 8 characters");
    if (!/[A-Z]/.test(password)) errors.push("One uppercase letter");
    if (!/[a-z]/.test(password)) errors.push("One lowercase letter");
    if (!/[0-9]/.test(password)) errors.push("One number");
    return errors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Age verification check
    if (ageVerification === "under16") {
      setShowAgeError(true);
      return;
    }

    if (!isFormValid) {
      return;
    }

    try {
      setIsSubmitting(true);
      clearError();

      // Create user profile with GDPR consent data
      const gdprConsent = {
        necessary: true,
        functional: true,
        analytics: marketingConsent,
        marketing: marketingConsent,
        consentDate: new Date(),
        privacyPolicyVersion: "3.0",
        ageVerified: true,
        ageVerificationDate: new Date(),
      };

      // If we have an anonymous user already, upgrade their account to
      // preserve UID + all conversations/visits/feed/postcards. Otherwise
      // create a fresh account.
      if (isUpgrade) {
        await upgradeAnonymousAccount(
          formData.email.trim(),
          formData.password,
          formData.displayName.trim() || undefined,
          gdprConsent
        );
      } else {
        await signUpWithEmail(
          formData.email.trim(),
          formData.password,
          formData.displayName.trim() || undefined,
          gdprConsent
        );
      }

      setSignupComplete({ method: "email", email: formData.email.trim() });
    } catch (error) {
      // Error is handled by the store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignUp = async () => {
    // The new flow: don't require the form's age + terms + privacy
    // checkboxes for Google sign-up. The user clicks "Continue with
    // Google" → Google popup → returns → our auth modal shows a
    // dedicated consent step (GoogleConsentStep) where they confirm
    // age and accept terms/privacy. This is cleaner UX than gating
    // the Google button on form fields nobody wants to fill out.
    try {
      setIsSubmitting(true);
      clearError();

      // No gdprConsent passed — that triggers the deferred-write
      // path in the store. The user's profile gets written when
      // they accept on the consent step.
      await signInWithGoogle(
        undefined,
        formData.displayName.trim() || undefined
      );

      // Don't call setSignupComplete here — the consent step will
      // either complete the signup or cancel it. Either way the
      // AuthModal reacts to pendingGoogleConsent state directly.
    } catch (error) {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordErrors = validatePassword(formData.password);
  const passwordsMatch = formData.password === formData.confirmPassword;
  // Lightweight format check — Firebase will be the authoritative
  // validator, but rejecting obvious typos client-side is faster UX.
  const isEmailFormatValid = (() => {
    const e = formData.email.trim();
    if (!e) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  })();
  const isFormValid =
    ageVerification === "over16" &&
    isEmailFormatValid &&
    formData.password &&
    passwordErrors.length === 0 &&
    passwordsMatch &&
    acceptedTerms &&
    acceptedPrivacy;
  // Google sign-up no longer requires the form's checkboxes to be
  // pre-filled. The button is always enabled — consent is collected
  // in the GoogleConsentStep that runs after the popup completes.
  const isGoogleSignUpEnabled = true;

  return (
    <>
      {signupComplete ? (
        <Box sx={{ py: 2 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              
              {t("Account created")}
            </Typography>
            {signupComplete.method === "email" ? (
              <Typography variant="body2">
                
                {t("We've sent a verification email to")}{" "}
                <strong>{signupComplete.email}</strong>{t(". Check your inbox to confirm your address. You can keep using the app while you wait.")}
              </Typography>
            ) : (
              <Typography variant="body2">
                
                {t("You're signed in with Google and ready to go.")}
              </Typography>
            )}
          </Alert>
          <Button
            fullWidth
            variant="contained"
            onClick={onSuccess}
            sx={{ py: 1.5 }}
          >
            
            {t("Continue")}
          </Button>
        </Box>
      ) : (
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {isUpgrade
            ? t("Create an account to upgrade your anonymous session and save your conversations")
            : "Create a new account to get started with Instant Tours"}
        </Typography>

        <TextField
          fullWidth
          label={t("Display Name (Optional)")}
          value={formData.displayName}
          onChange={handleInputChange("displayName")}
          disabled={isSubmitting}
          margin="normal"
          autoComplete="name"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          type="email"
          label={t("Email")}
          value={formData.email}
          onChange={handleInputChange("email")}
          disabled={isSubmitting}
          margin="normal"
          required
          autoComplete="email"
          error={formData.email.length > 0 && !isEmailFormatValid}
          helperText={
            formData.email.length > 0 && !isEmailFormatValid
              ? "Please enter a valid email address"
              : undefined
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          type={showPassword ? "text" : "password"}
          label={t("Password")}
          value={formData.password}
          onChange={handleInputChange("password")}
          disabled={isSubmitting}
          margin="normal"
          required
          autoComplete="new-password"
          error={formData.password.length > 0 && passwordErrors.length > 0}
          helperText={
            formData.password.length > 0 && passwordErrors.length > 0
              ? `Required: ${passwordErrors.join(", ")}`
              : undefined
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={isSubmitting}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          type={showConfirmPassword ? "text" : "password"}
          label={t("Confirm Password")}
          value={formData.confirmPassword}
          onChange={handleInputChange("confirmPassword")}
          disabled={isSubmitting}
          margin="normal"
          required
          autoComplete="new-password"
          error={formData.confirmPassword.length > 0 && !passwordsMatch}
          helperText={
            formData.confirmPassword.length > 0 && !passwordsMatch
              ? "Passwords do not match"
              : undefined
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  disabled={isSubmitting}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* GDPR Age Verification Section */}
        <Box
          sx={{
            mt: 3,
            mb: 2,
            p: 2,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" gutterBottom>
            
            {t("Age Verification (Required)")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            
            {t("To comply with European data protection laws, we must verify that you meet the minimum age requirement.")}
          </Typography>

          <FormControl component="fieldset" error={showAgeError} required>
            <FormLabel component="legend">{t("Please confirm your age:")}</FormLabel>
            <RadioGroup
              value={ageVerification}
              onChange={handleAgeChange}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="over16"
                control={<Radio />}
                label={t("I am 16 years of age or older")}
                disabled={isSubmitting}
              />
              <FormControlLabel
                value="under16"
                control={<Radio />}
                label={t("I am under 16 years of age")}
                disabled={isSubmitting}
              />
            </RadioGroup>
          </FormControl>

          {showAgeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{t("Account Creation Restricted:")}</strong>  {t("You must be at least 16 years old to create an account in compliance with European GDPR regulations. If you are under 16, you may use our service as a guest without creating an account.")}
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Legal Agreement Section */}
        <Box
          sx={{
            mt: 3,
            mb: 2,
            p: 2,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" gutterBottom>
            
            {t("Legal Agreements (Required)")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            
            {t("You must read and accept all legal documents to create an account:")}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={isSubmitting}
                color="primary"
                required
              />
            }
            label={
              <Typography variant="body2">
                <strong>{t("Required:")}</strong>  {t("I have read and agree to the")}{" "}
                <Link
                  component="button"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowTermsDialog(true);
                  }}
                  sx={{ textDecoration: "underline", fontWeight: 600 }}
                >
                  
                  {t("Terms of Service")}
                </Link>
              </Typography>
            }
            sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                disabled={isSubmitting}
                color="primary"
                required
              />
            }
            label={
              <Typography variant="body2">
                <strong>{t("Required:")}</strong>  {t("I have read and agree to the")}{" "}
                <Link
                  component="button"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowPrivacyDialog(true);
                  }}
                  sx={{ textDecoration: "underline", fontWeight: 600 }}
                >
                  
                  {t("Privacy Policy")}
                </Link>
              </Typography>
            }
            sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}
          />

          {/* Optional Marketing Consent */}
          <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              
              {t("Optional Consent")}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  disabled={isSubmitting}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  
                  {t("I consent to receive service updates and marketing communications (you can withdraw this consent at any time)")}
                </Typography>
              }
              sx={{ display: "flex", alignItems: "flex-start" }}
            />
          </Box>

          {(!acceptedTerms ||
            !acceptedPrivacy ||
            ageVerification !== "over16") && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{t("Registration Incomplete:")}</strong>  {t("You must be at least 16 years old and accept both the Terms of Service and Privacy Policy to create an account.")}
              </Typography>
            </Alert>
          )}
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!isFormValid || isSubmitting || showAgeError}
          sx={{ mt: 2, mb: 2, py: 1.5 }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : isUpgrade ? (
            t("Create Account & Upgrade")
          ) : (
            "Create Account"
          )}
        </Button>

        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            
            {t("or")}
          </Typography>
        </Divider>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<Google />}
          onClick={handleGoogleSignUp}
          disabled={!isGoogleSignUpEnabled || isSubmitting || showAgeError}
          sx={{ py: 1.5, mb: 2 }}
        >
          {isUpgrade
            ? t("Continue with Google & Upgrade")
            : t("Continue with Google")}
        </Button>

        {isUpgrade && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                
                {t("or")}
              </Typography>
            </Divider>

            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              
              {t("Already have an account?")}
            </Typography>

            <Button
              variant="outlined"
              fullWidth
              onClick={onSwitchToLogin}
              disabled={isSubmitting}
              sx={{ py: 1.5 }}
            >
              
              {t("Sign In Instead")}
            </Button>
          </>
        )}

        {!isUpgrade && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            
            {t("Already have an account?")}{" "}
            <Link
              component="button"
              type="button"
              onClick={onSwitchToLogin}
              disabled={isSubmitting}
              sx={{ textDecoration: "none" }}
            >
              
              {t("Sign in here")}
            </Link>
          </Typography>
        )}
      </Box>
      )}

      {/* Privacy Policy Dialog */}
      <Dialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "90vh" },
        }}
      >
        <DialogTitle>
          <Typography variant="h5">{t("Privacy Policy")}</Typography>
          <Typography variant="body2" color="text.secondary">
            
            {t("Please read carefully - acceptance is required for account creation")}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <Privacy />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
            <Button
              onClick={() => setShowPrivacyDialog(false)}
              variant="outlined"
              fullWidth
            >
              
              {t("Close")}
            </Button>
            <Button
              onClick={() => {
                setAcceptedPrivacy(true);
                setShowPrivacyDialog(false);
              }}
              variant="contained"
              disabled={acceptedPrivacy}
              fullWidth
            >
              {acceptedPrivacy ? "Accepted" : "Accept Privacy Policy"}
            </Button>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            
            {t("You must accept the Privacy Policy to create an account")}
          </Typography>
        </DialogActions>
      </Dialog>

      {/* Terms of Service Dialog */}
      <Dialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "90vh" },
        }}
      >
        <DialogTitle>
          <Typography variant="h5">{t("Terms of Service")}</Typography>
          <Typography variant="body2" color="text.secondary">
            
            {t("Please read carefully - acceptance is required for account creation")}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <Terms />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
            <Button
              onClick={() => setShowTermsDialog(false)}
              variant="outlined"
              fullWidth
            >
              
              {t("Close")}
            </Button>
            <Button
              onClick={() => {
                setAcceptedTerms(true);
                setShowTermsDialog(false);
              }}
              variant="contained"
              disabled={acceptedTerms}
              fullWidth
            >
              {acceptedTerms ? "Accepted" : "Accept Terms of Service"}
            </Button>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            
            {t("You must accept the Terms of Service to create an account")}
          </Typography>
        </DialogActions>
      </Dialog>
    </>
  );
};
